import { wccModules } from 'enums/wccModules';
import { Func } from 'interfaces/func';
import { inject, injectable } from 'inversify';
import { PureComputed, Subscribable, SubscribableOrNullableValue } from 'knockout';
import TagsManagerBase from 'managers/tags/base';
import ReadOnlyCollectionTagsManager, { ReadOnlyCollectionTagsManagerConfig } from 'managers/tags/readonlyCollection';
import { EffectsContainer } from 'mixins/withEffects';
import { Tag } from 'models/tag';
import './list.html';
import './list.scss';

components.preload('tag', 'entity-tag');

interface TagsListConfig {
    manager?: SubscribableOrNullableValue<TagsManagerBase>
    tags?: SubscribableOrNullableValue<Array<Tag>>
    simpleUI?: SubscribableOrNullableValue<boolean>
    readOnly?: SubscribableOrNullableValue<boolean>
    remove?: Action<[Tag]> | Func<Promise<void>, [Tag]>
    click?: Action<[Tag]>
}

@injectable()
export default class TagsListVM {
    private clickHandler: (tag: Tag) => void

    labels = labels

    manager: Subscribable<TagsManagerBase>

    tags: Subscribable<Array<Tag>>
    oActiveTag = ko.observable<Tag | undefined>();
    loadingTags: Subscribable<boolean>
    updatingTags: Subscribable<boolean>
    hasTags: Subscribable<boolean>
    showNoTags: Subscribable<boolean>
    
    readOnly: Subscribable<boolean>
    ready: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) model: TagsListConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.effects) private effects: EffectsContainer
    ) {
        const readOnlyMode = ko.flattenComputed(model.readOnly, false);
        const simpleUIMode = ko.flattenComputed(model.simpleUI, true);

        const defaultManager = ko.pureComputed(() => ko.ignoreDependencies(() => this.getDefaultManager({
            tags: model.tags,
            remove: model.remove
        })));

        this.manager = ko.flattenComputed(model.manager, defaultManager);

        this.tags = this.manager.pluck(m => m.tags, []).sortBy(t => t.tagValue());
        this.loadingTags = this.manager.pluck(m => m.loading, true);
        this.updatingTags = this.manager.pluck(m => m.updating, false);
        this.hasTags = this.tags.any();
        this.showNoTags = ko.pureComputed(() => !simpleUIMode() && !this.hasTags());

        const editAllowed = this.manager.pluck(m => m.allowEdit, false);
        const removeAllowed = ko.pureComputed(() => this.manager()?.allowRemove() ?? false);
        
        this.readOnly = ko.pureComputed(() => readOnlyMode() || !editAllowed() && !removeAllowed());

        this.ready = ko.pureComputed(() => !this.loadingTags());

        this.clickHandler = model.click ?? _.noop;

        ko.applyBindingsToNode(info.element, {
            'css': {
                'tags-list': true
            }
        }, this);
    }

    onClick = (tag: Tag) => this.clickHandler(tag)

    activateTag = (tag: Tag) => {
        this.oActiveTag()?.oIsActive(false);
        tag.oIsActive(true);
        this.oActiveTag(tag);
    }

    private getDefaultManager(config: ReadOnlyCollectionTagsManagerConfig): TagsManagerBase {
        return this.effects.register(new ReadOnlyCollectionTagsManager(config));
    }
}